import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Profile from './views/Profile';
import Login from './views/Login';
import MainLayout from './components/MainLayout';
import AuthLayout from './components/AuthLayout';
import useAuth from './hooks/useAuth';
import { CircularProgress, Backdrop } from '@mui/material';

/**
 * Este componente lida com rotas privadas (isPrivate).
 * Se não estiver autenticado, redireciona para /login
 * preservando a URL que o usuário tentou acessar.
 */
function CustomRoute({ isPrivate, element }: { isPrivate?: boolean; element: React.ReactElement }) {
  const { authenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    // Enquanto está carregando, exibe um indicador de carregamento
    return (
      <Backdrop open={true} sx={{ zIndex: 1300 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  if (isPrivate && !authenticated) {
    // Preserva a rota/URL atual como ?redirect=..., 
    // para que, após login, a tela de login possa encaminhar de volta.
    const desiredPath = `${location.pathname}${location.search}`;
    const encodedPath = encodeURIComponent(desiredPath);

    return <Navigate to={`/login?redirect=${encodedPath}`} replace />;
  }

  return element;
}

const AppRoutes: React.FC = () => {
  const { authenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    // Enquanto está carregando, exibe um indicador de carregamento
    return (
      <Backdrop open={true} sx={{ zIndex: 1300 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  return (
    <Routes>
      {/* Rota raiz, redireciona para /login ou /perfil, mantendo eventual query string */}
      <Route
        path="/"
        element={
          authenticated ? (
            <Navigate to={`/perfil${location.search}`} replace />
          ) : (
            <Navigate to={`/login${location.search}`} replace />
          )
        }
      />

      {/* Rota de login - se já estiver logado, manda para /perfil, preservando query */}
      <Route
        path="/login"
        element={
          authenticated ? (
            <Navigate to={`/perfil${location.search}`} replace />
          ) : (
            <AuthLayout>
              <Login />
            </AuthLayout>
          )
        }
      />

      {/* Rotas protegidas */}
      <Route
        path="/perfil"
        element={
          <CustomRoute
            isPrivate={true}
            element={
              <MainLayout>
                <Profile />
              </MainLayout>
            }
          />
        }
      />

      {/* Rota para 404 */}
      <Route
        path="*"
        element={
          <MainLayout>
            <div>Página não encontrada</div>
          </MainLayout>
        }
      />
    </Routes>
  );
};

export default AppRoutes;